// The typed API client — one fetch wrapper, the server's error envelope
// surfaced as ApiError, bearer token refreshed transparently. Relative
// /api/v1 base: same origin in production (hosting rewrite) and under
// `cargo run`; Vite dev proxies it to the slot's API port.

import { freshToken } from './auth';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
  }
}

type Params = Record<string, string | number | boolean | undefined>;

async function request<T>(
  method: string,
  path: string,
  opts: { body?: unknown; params?: Params; auth?: boolean } = {},
): Promise<T> {
  const url = new URL('/api/v1' + path, window.location.origin);
  for (const [k, v] of Object.entries(opts.params ?? {})) {
    if (v !== undefined) url.searchParams.set(k, String(v));
  }
  const headers: Record<string, string> = {};
  if (opts.auth !== false) headers['Authorization'] = `Bearer ${await freshToken()}`;
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  const resp = await fetch(url, {
    method,
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
  if (!resp.ok) {
    let code = 'unknown';
    let message = resp.statusText;
    try {
      const e = (await resp.json())?.error;
      code = e?.code ?? code;
      message = e?.message ?? message;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(resp.status, code, message);
  }
  if (resp.status === 204) return undefined as T;
  const ct = resp.headers.get('content-type') ?? '';
  if (!ct.startsWith('application/json')) return (await resp.blob()) as T;
  return (await resp.json()) as T;
}

export const api = {
  get: <T>(path: string, params?: Params) => request<T>('GET', path, { params }),
  post: <T>(path: string, body?: unknown, params?: Params) => request<T>('POST', path, { body, params }),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, { body }),
  del: <T>(path: string) => request<T>('DELETE', path),
};

// ── The wire types the shared screens consume — hand-authored against the
// server's models (the shapes the testbed proves). ───────────────────────

export type Me = {
  userId: string;
  userType: 'portal' | 'customer';
  email: string | null;
  displayName: string | null;
  hasPassword: boolean;
  isSuperAdmin: boolean;
  memberships: { restaurantId: string; restaurantName: string; role: string }[];
  storeMemberships: { restaurantId: string; storeId: string; storeName: string; role: string }[];
};

export type Restaurant = { restaurantId: string; name: string; logoUrl: string | null; timezone: string; country: string };
export type Store = {
  restaurantId: string;
  storeId: string;
  name: string;
  timezone: string;
  address: string;
  phoneE164: string;
  pickupEnabled: boolean;
  deliveryEnabled: boolean;
  dineInEnabled: boolean;
  temporarilyClosed: boolean;
  permanentlyClosed: boolean;
  dayCloseTime: string;
};

export type OrderSource = 'website' | 'pos' | 'google' | 'uber_eats';
export type CollectionType = 'pickup' | 'delivery' | 'dine_in';
export type OrderStatus = 'new' | 'accepted' | 'in_progress' | 'completed' | 'rejected' | 'cancelled';
export type PaymentState = 'unpaid' | 'partially_paid' | 'paid' | 'partially_refunded' | 'refunded';

export type ChosenModifier = { rowId: string; modifierName: string; optionName: string; extraChargeInCents: number; sortOrder: number; modifierId: string; optionId: string };
export type OrderToppingRow = { rowId: string; action: 'default' | 'add' | 'remove'; toppingName: string; quantity: number; sortOrder: number; unitPriceInCents: number; totalPriceInCents: number; toppingId: string };
export type OrderPizza = {
  pizzaId: string;
  name: string;
  sizeName: string;
  baseName: string | null;
  unitPriceInCents: number;
  baseExtraInCents: number;
  itemId: string;
  sizeId: string;
  baseId: string | null;
  toppings: OrderToppingRow[];
  modifiers: ChosenModifier[];
};
export type OrderHalfAndHalf = {
  halfAndHalfId: string;
  sizeName: string;
  baseName: string | null;
  baseExtraInCents: number;
  splitExtraInCents: number;
  sizeId: string;
  baseId: string | null;
  first: OrderPizza;
  second: OrderPizza;
};
export type BundlePick = {
  pickId: string;
  slotName: string;
  name: string | null;
  quantity: number;
  sortOrder: number;
  itemPriceInCents: number;
  extraChargeInCents: number;
  slotId: string;
  itemId: string | null;
  pickType: 'generic' | 'pizza' | 'half_and_half';
  modifiers?: ChosenModifier[];
  pizza?: OrderPizza;
  halfAndHalf?: OrderHalfAndHalf;
};

/** One line of the frozen tree. The type-specific half is flattened. */
export type OrderLine = {
  orderItemId: string;
  itemType: 'generic' | 'custom' | 'pizza' | 'half_and_half' | 'bundle';
  name: string;
  description: string | null;
  quantity: number;
  comments: string | null;
  sortOrder: number;
  unitPriceInCents: number;
  lineTotalInCents: number;
  discountInCents: number;
  isGstApplicable: boolean;
  sentAt: string | null;
  voidedAt: string | null;
  voidReason: string | null;
  voidedBy: string | null;
  itemId: string | null;
  categoryId: string | null;
  categoryName: string | null;
  externalLineId: string | null;
  modifiers: ChosenModifier[];
  pizza?: OrderPizza;
  halfAndHalf?: OrderHalfAndHalf;
  picks?: BundlePick[];
};

export type OrderDiscount = { rowId: string; discountType: string; label: string; amountInCents: number; percentBp: number | null; couponCode: string | null; employeeId: string | null };
export type OrderSurcharge = { rowId: string; surchargeType: string; label: string; amountInCents: number; percentBp: number | null; surchargeRuleId: string | null; employeeId: string | null };

export type DeliveryDetails = {
  addressLine: string;
  suburb: string;
  postcode: string;
  state: string;
  lat: number | null;
  lng: number | null;
  deliveryAreaId: string | null;
  chargeInCents: number;
  courier: 'in_house' | 'uber_direct';
  courierJobId: string | null;
  courierStatus: string | null;
  trackingUrl: string | null;
  courierQuoteInCents: number | null;
  proofOfDeliveryUrl: string | null;
};

export type Order = {
  orderId: string;
  source: OrderSource;
  collectionType: CollectionType;
  status: OrderStatus;
  statusReason: string | null;
  placedAt: string;
  requestedFor: string | null;
  businessDate: string;
  version: number;
  isEdited: boolean;
  customerId: string | null;
  customerName: string | null;
  customerPhoneE164: string | null;
  employeeId: string | null;
  posTerminalId: string | null;
  subtotalInCents: number;
  discountTotalInCents: number;
  surchargeTotalInCents: number;
  deliveryFeeInCents: number;
  gstInCents: number;
  grandTotalInCents: number;
  couponCode: string | null;
  loyaltyCardId: string | null;
  docketLabel: string | null;
  invoiceNumber: string | null;
  externalOrderId: string | null;
  currencyCode: string;
  clientOrderId: string | null;
  timezone: string;
  comments: string | null;
  paidTotalInCents: number;
  refundedTotalInCents: number;
  tipTotalInCents: number;
  paymentState: PaymentState;
  isPayLater: boolean;
  owner: string;
  createdAt: string;
  updatedAt: string;
  modifiedAt: number;
  modifiedBy: string;
  delivery: DeliveryDetails | null;
  dineIn: { tableId: string | null; tableName: string; guestCount: number | null } | null;
  lines: OrderLine[];
  discounts: OrderDiscount[];
  surcharges: OrderSurcharge[];
};

export type OrderEvent = {
  seq: number;
  epoch: number;
  eventType: string;
  actorType: string;
  actorId: string | null;
  note: string | null;
  occurredAt: string;
};

export type Payment = {
  paymentId: string;
  txnType: 'payment' | 'refund' | 'reversal';
  method: 'cash' | 'card' | 'moto' | 'online' | 'custom';
  provider: string | null;
  status: 'pending' | 'success' | 'failed' | 'cancelled' | 'declined';
  amountInCents: number;
  tipInCents: number;
  surchargeInCents: number;
  billCreditInCents: number;
  cardBrand: string | null;
  cardLast4: string | null;
  customPaymentName: string | null;
  splitGroupId: string | null;
  refundOfPaymentId: string | null;
  reason: string | null;
  employeeId: string | null;
  occurredAt: string;
};

export type PaymentsView = {
  orderId: string;
  orderVersion: number;
  grandTotalInCents: number;
  paidTotalInCents: number;
  refundedTotalInCents: number;
  tipTotalInCents: number;
  refundableInCents: number;
  outstandingInCents: number;
  paymentState: PaymentState;
  isPayLater: boolean;
  payments: Payment[];
  groups: { groupId: string; splitType: string; splitCount: number | null; totalAmountInCents: number; isCancelled: boolean; paidInCents: number; isComplete: boolean; sharesInCents: number[] }[];
};

/** Every report shares this shape: typed columns, rows as objects, totals. */
export type ReportColumn = { key: string; label: string; columnType: string };
export type Report = {
  report: string;
  range: { from: string; to: string };
  timezone: string;
  groupBy: string | null;
  generatedAt: string;
  columns: ReportColumn[];
  rows: Record<string, string | number | null>[];
  totals: Record<string, string | number | null> | null;
  summary: Record<string, string | number | null> | null;
  compare: { range: { from: string; to: string }; table: { columns: ReportColumn[]; rows: Record<string, string | number | null>[]; totals: Record<string, string | number | null> | null } } | null;
};

export type Booking = {
  bookingId: string;
  status: string;
  source: string;
  bookingDate: string;
  bookingTime: string;
  sessionNo: number | null;
  pax: number;
  customerName: string;
  customerPhoneE164: string;
  tableId: string | null;
  tableName: string | null;
  notes: string | null;
};

export type Terminal = { terminalId: string; name: string; posId: string | null; hasPassword: boolean; active: boolean; docketPrefix: string | null };

export const money = (cents: number): string =>
  (cents / 100).toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });

export const timeOf = (iso: string, tz?: string): string =>
  new Date(iso).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', timeZone: tz });

export const dateTimeOf = (iso: string, tz?: string): string =>
  new Date(iso).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', timeZone: tz });

export const label = (s: string): string => s.replace(/_/g, ' ');
